import React, { useState } from 'react';
import { 
  FaFacebook, FaTwitter, FaYoutube, FaPhone, FaMapMarkerAlt, 
  FaBars, FaTimes, FaCalendarAlt, FaUser, FaEnvelope, 
  FaClock, FaArrowRight, FaCheckCircle, FaWhatsapp,
  FaStethoscope, FaHeartbeat
} from 'react-icons/fa';

// AppointmentPage Component - Fixed close functionality
const AppointmentPage = ({ onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    date: '',
    time: '',
    message: ''
  });
  
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  React.useEffect(() => {
    const today = new Date();
    const formattedDate = today.toISOString().split('T')[0];
    const hours = today.getHours();
    const nextHour = hours < 17 ? (hours + 1) : 9;
    const formattedTime = nextHour < 12 ? `${nextHour}:00 AM` : 
                         nextHour === 12 ? '12:00 PM' : 
                         `${nextHour - 12}:00 PM`;
    
    setFormData(prev => ({ 
      ...prev, 
      date: formattedDate,
      time: formattedTime
    }));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    setTimeout(() => {
      setSubmitted(true);
      setIsLoading(false);
      
      setTimeout(() => {
        if (onClose) {
          console.log('Auto-closing appointment page');
          onClose();
        }
      }, 5000);
    }, 1500);
  };

  const openWhatsApp = () => {
    const message = `Hello City Clinic,\n\nI'd like to book an appointment.\n\n• Name: ${formData.name || 'Not provided'}\n• Phone: ${formData.phone || 'Not provided'}\n• Email: ${formData.email || 'Not provided'}\n• Preferred Date: ${formData.date || 'Not provided'}\n• Preferred Time: ${formData.time || 'Not provided'}\n\nThank you!`;
    const url = `https://wa.me/918002280222?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  // Handle close click
  const handleCloseClick = () => {
    console.log('Close button clicked');
    if (onClose) {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-gradient-to-br from-blue-50 via-white to-blue-50 overflow-y-auto">
      {/* Close Button - Now clickable */}
      <button
        onClick={handleCloseClick}
        className="fixed top-4 right-4 md:top-6 md:right-6 z-[110] p-3 bg-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 border border-gray-200"
        aria-label="Close appointment form"
      >
        <FaTimes className="text-xl md:text-2xl text-gray-600 hover:text-red-500" />
      </button>

      <div className="min-h-screen flex items-center justify-center p-4 md:p-6">
        <div className="w-full max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 bg-gradient-to-r from-blue-600 to-blue-700 rounded-full mb-4 shadow-xl">
              <FaStethoscope className="text-2xl md:text-3xl text-white" />
            </div>
            <h1 className="text-2xl md:text-4xl font-bold text-gray-800 mb-2">
              Book Your Appointment
            </h1>
            <p className="text-gray-600 text-sm md:text-base">
              Schedule your consultation with Dr. Shailendra Mohan Shukla
            </p>
          </div>

          <div className="bg-white rounded-xl md:rounded-2xl shadow-lg md:shadow-xl overflow-hidden">
            {submitted ? (
              <div className="p-6 md:p-8 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 md:w-24 md:h-24 bg-gradient-to-r from-green-500 to-green-600 rounded-full mb-4 animate-bounce">
                  <FaCheckCircle className="text-3xl md:text-5xl text-white" />
                </div>
                <h2 className="text-xl md:text-3xl font-bold text-gray-800 mb-3">
                  Appointment Requested!
                </h2>
                <p className="text-gray-600 md:text-lg mb-6">
                  Thank you for choosing City Clinic. Our team will contact you within 24 hours.
                </p>
                
                <div className="p-4 md:p-6 bg-gradient-to-r from-blue-50 to-green-50 rounded-lg border border-green-200 max-w-md mx-auto">
                  <p className="font-semibold text-gray-700 mb-1">Emergency Contact</p>
                  <a 
                    href="tel:+918002280222" 
                    className="text-red-600 font-bold text-lg md:text-xl hover:underline block"
                  >
                    +91 8002280222
                  </a>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="p-4 md:p-8">
                <div className="space-y-4 md:space-y-6">
                  <div className="group">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <div className="flex items-center">
                        <FaUser className="mr-2 text-blue-600" />
                        Full Name *
                      </div>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                      placeholder="Enter your full name"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                    <div className="group">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <div className="flex items-center">
                          <FaPhone className="mr-2 text-blue-600" />
                          Phone Number *
                        </div>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                        placeholder="+91 8002280222"
                      />
                    </div>

                    <div className="group">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <div className="flex items-center">
                          <FaEnvelope className="mr-2 text-blue-600" />
                          Email Address *
                        </div>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                        placeholder="you@example.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                    <div className="group">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <div className="flex items-center">
                          <FaCalendarAlt className="mr-2 text-blue-600" />
                          Preferred Date *
                        </div>
                      </label>
                      <input
                        type="date"
                        name="date"
                        value={formData.date}
                        onChange={handleChange}
                        required
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                      />
                    </div>

                    <div className="group">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <div className="flex items-center">
                          <FaClock className="mr-2 text-blue-600" />
                          Preferred Time *
                        </div>
                      </label>
                      <select
                        name="time"
                        value={formData.time}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                      >
                        <option value="">Select time</option>
                        <option value="9:00 AM">9:00 AM - 10:00 AM</option>
                        <option value="10:00 AM">10:00 AM - 11:00 AM</option>
                        <option value="11:00 AM">11:00 AM - 12:00 PM</option>
                        <option value="2:00 PM">2:00 PM - 3:00 PM</option>
                        <option value="3:00 PM">3:00 PM - 4:00 PM</option>
                        <option value="4:00 PM">4:00 PM - 5:00 PM</option>
                        <option value="5:00 PM">5:00 PM - 6:00 PM</option>
                      </select>
                    </div>
                  </div>

                  <div className="group">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Medical Concern / Symptoms
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows="3"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200 resize-none"
                      placeholder="Briefly describe your symptoms or concerns..."
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <button
                      type="submit"
                      disabled={isLoading}
                      className={`w-full ${isLoading ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'} text-white py-3 px-4 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center space-x-2`}
                    >
                      {isLoading ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span>Processing...</span>
                        </>
                      ) : (
                        <>
                          <span>Book Appointment</span>
                          <FaArrowRight className="ml-2" />
                        </>
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={openWhatsApp}
                      className="w-full bg-green-500 hover:bg-green-600 text-white py-3 px-4 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center space-x-2"
                    >
                      <FaWhatsapp className="text-lg" />
                      <span>Book via WhatsApp</span>
                    </button>
                  </div>

                  <div className="pt-4 border-t border-gray-200">
                    <div className="flex flex-col md:flex-row items-center justify-between text-sm text-gray-600">
                      <div className="flex items-center mb-2 md:mb-0">
                        <FaHeartbeat className="mr-2 text-red-500" />
                        <span className="font-medium">24/7 Emergency:</span>
                        <a 
                          href="tel:+918002280222" 
                          className="text-red-600 font-bold ml-2 hover:underline"
                        >
                          +91 8002280222
                        </a>
                      </div>
                      <div className="flex items-center">
                        <FaMapMarkerAlt className="mr-2 text-blue-500" />
                        <span>Medical Chowk, Ranchi</span>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            )}
          </div>

          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              City Clinic • Dr. Shailendra Mohan Shukla • Expert Urological Care
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Navbar Component
const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAppointmentPageOpen, setIsAppointmentPageOpen] = useState(false);

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'Dr. Shailendra Mohan Shukla', path: 'about-doctor' },
    { name: 'Facilities', path: 'facility' },
    { name: 'Blog', path: 'blog' },
    { name: 'Our Gallery', path: 'gallery' },
    { name: 'Contact Us', path: 'contact' },
  ];

  // This function properly handles closing the appointment page
  const handleCloseAppointment = () => {
    console.log('Closing appointment page');
    setIsAppointmentPageOpen(false);
  };

  return (
    <div className="font-sans">
      {/* Render Appointment Page */}
      {isAppointmentPageOpen && (
        <AppointmentPage onClose={handleCloseAppointment} />
      )}

      {/* Desktop Top Bar */}
      <div className="bg-gradient-to-r from-sky-500 to-red-500 text-white py-2 px-4 hidden md:block">
        <div className="container mx-auto">
          <div className="flex flex-wrap justify-between items-center">
            <div className="text-sm font-medium animate-pulse">
              🏥 Welcome to City Clinic - Best Urology Care in Ranchi
            </div>

            <div className="flex items-center space-x-4 lg:space-x-6 flex-wrap">
              <div className="flex items-center space-x-3 lg:space-x-4">
                <a href="https://www.facebook.com/people/Dr-Shailendra-Mohan-Shukla/61577993564809/" 
                   className="text-white hover:text-gray-200 transition-all duration-300 transform hover:scale-110 hover:-translate-y-1"
                   target="_blank" 
                   rel="noopener noreferrer">
                  <FaFacebook className="text-lg" />
                </a>
                <a href="#" className="text-white hover:text-gray-200 transition-all duration-300 transform hover:scale-110 hover:-translate-y-1">
                  <FaTwitter className="text-lg" />
                </a>
                <a href="#" className="text-white hover:text-gray-200 transition-all duration-300 transform hover:scale-110 hover:-translate-y-1">
                  <FaYoutube className="text-lg" />
                </a>
              </div>

              <div className="h-4 w-px bg-white/30 hidden lg:block"></div>

              <div className="hidden lg:flex items-center space-x-2 text-sm">
                <FaMapMarkerAlt className="flex-shrink-0" />
                <span className="hidden xl:inline">City Clinic RIMS Medical Chowk, Ranchi, Jharkhand</span>
                <span className="xl:hidden">Medical Chowk, Ranchi</span>
              </div>

              <div className="h-4 w-px bg-white/30 hidden lg:block"></div>

              <div className="flex items-center space-x-2 text-sm">
                <FaPhone className="animate-bounce flex-shrink-0" />
                <a 
                  href="tel:+918002280222" 
                  className="font-semibold hover:text-gray-200 transition-colors duration-300 whitespace-nowrap"
                >
                  +91 8002280222
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Top Bar */}
      <div className="bg-gradient-to-r from-sky-500 to-red-500 text-white py-2 px-4 md:hidden">
        <div className="container mx-auto">
          <div className="flex justify-between items-center">
            <div className="text-xs font-medium">
              🏥 City Clinic Ranchi
            </div>
            <div className="flex items-center space-x-3">
              <a href="https://www.facebook.com/people/Dr-Shailendra-Mohan-Shukla/61577993564809/" 
                 className="text-white hover:text-gray-200"
                 target="_blank" 
                 rel="noopener noreferrer">
                <FaFacebook className="text-base" />
              </a>
              <a href="tel:+918002280222" className="text-white hover:text-gray-200">
                <FaPhone className="text-base animate-bounce" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <nav className="sticky top-0 z-50 bg-white shadow-lg">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center h-16 md:h-20">
            {/* Logo */}
            <div className="flex items-center space-x-3">
              <div className="flex items-center">
                <img 
                  src="/logo.png" 
                  className="w-12 h-12 md:w-16 md:h-16 object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    const fallback = document.createElement('div');
                    fallback.className = 'w-12 h-12 md:w-16 md:h-16 bg-gradient-to-r from-sky-400 to-red-500 rounded-lg flex items-center justify-center';
                    fallback.innerHTML = '<span class="text-white font-bold text-lg md:text-xl">CC</span>';
                    e.target.parentNode.insertBefore(fallback, e.target.nextSibling);
                  }}
                />
              </div>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center justify-center flex-1">
              <div className="flex items-center space-x-6 justify-center">
                {navItems.map((item, index) => (
                  <a
                    key={index}
                    href={item.path}
                    className="text-gray-700 hover:text-sky-600 font-medium transition-all duration-300 hover:translate-y-[-2px] relative group/nav text-sm"
                  >
                    {item.name}
                    <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-sky-500 to-red-500 transition-all duration-300 group-hover/nav:w-full"></span>
                  </a>
                ))}
              </div>
            </div>

            {/* Right Side - Small Appointment Button */}
            <div className="hidden lg:flex items-center">
              <button
                onClick={() => setIsAppointmentPageOpen(true)}
                className="px-5 py-2.5 bg-gradient-to-r from-sky-500 to-red-500 text-white font-semibold rounded-lg hover:from-sky-600 hover:to-red-600 transition-all duration-300 transform hover:scale-105 hover:shadow-md flex items-center space-x-2 text-sm shadow-sm"
              >
                <FaCalendarAlt className="text-sm" />
                <span>Appointment</span>
              </button>
            </div>

            <div className="hidden md:flex lg:hidden items-center">
              <button
                onClick={() => setIsAppointmentPageOpen(true)}
                className="px-4 py-2 bg-gradient-to-r from-sky-500 to-red-500 text-white font-semibold rounded-lg hover:from-sky-600 hover:to-red-600 transition-all duration-300 flex items-center space-x-2 text-sm"
              >
                <FaCalendarAlt className="text-xs" />
                <span>Book</span>
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden text-gray-700 hover:text-sky-600 transition-all duration-300"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? (
                <FaTimes className="text-xl md:text-2xl" />
              ) : (
                <FaBars className="text-xl md:text-2xl" />
              )}
            </button>
          </div>

          {/* Mobile Menu */}
          <div
            className={`lg:hidden overflow-hidden transition-all duration-500 ease-in-out ${
              isMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
            }`}
          >
            <div className="py-4 border-t">
              {navItems.map((item, index) => (
                <a
                  key={index}
                  href={item.path}
                  className="block px-4 py-3 text-gray-700 hover:text-sky-600 font-medium hover:bg-gray-50 rounded-lg transition-all duration-300 text-sm"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </a>
              ))}
              
              <div className="mt-4 space-y-3">
                <button
                  onClick={() => {
                    setIsAppointmentPageOpen(true);
                    setIsMenuOpen(false);
                  }}
                  className="w-full px-4 py-3 bg-gradient-to-r from-sky-500 to-red-500 text-white font-semibold rounded-lg hover:from-sky-600 hover:to-red-600 transition-all duration-300 flex items-center justify-center space-x-2"
                >
                  <FaCalendarAlt />
                  <span className="text-sm">Book Appointment</span>
                </button>
                
                <a 
                  href="tel:+918002280222"
                  className="block px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-300 flex items-center justify-center space-x-2"
                >
                  <FaPhone />
                  <span className="text-sm">Emergency: +91 8002280222</span>
                </a>
                
                <div className="flex items-center justify-center space-x-2 text-sm text-gray-600">
                  <FaMapMarkerAlt className="text-sky-600 flex-shrink-0" />
                  <span className="text-center">Medical Chowk, Ranchi</span>
                </div>
              </div>
              
              <div className="mt-4 flex justify-center space-x-6">
                <a href="https://www.facebook.com/people/Dr-Shailendra-Mohan-Shukla/61577993564809/" 
                   className="text-gray-600 hover:text-sky-600 transition-all duration-300"
                   target="_blank" 
                   rel="noopener noreferrer">
                  <FaFacebook className="text-lg" />
                </a>
                <a href="#" className="text-gray-600 hover:text-sky-600 transition-all duration-300">
                  <FaTwitter className="text-lg" />
                </a>
                <a href="#" className="text-gray-600 hover:text-sky-600 transition-all duration-300">
                  <FaYoutube className="text-lg" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </nav>
    </div>
  );
};

export default Navbar; 